import { DEAL_TYPE_ID, QUOTE_TYPE_ID } from 'customer-data-objects/constants/ObjectTypeIds';
import { useMemo } from 'react';
import { BillingConversionError } from '../types/validation';
import { getObjectConversionValidation } from '../utils/objectValidationHelpers';
import { useFetchConvertableObjectInfo } from './useFetchConvertableObjectInfo';
import { usePortalCurrencies } from './usePortalCurrencies';
/**
 * Validates the object is supported for the billing wizard.
 * @param objectTypeId - Object Type from customer-data-objects/constants/ObjectTypeIds
 * @param objectId - The Id of the object to validate.
 * @returns An array of validation errors.
 */
export function useValidateObjectForBillingConversion({
  objectTypeId,
  objectId,
  hasScope
}) {
  const hasAccess = hasScope('subscriptions-access');
  const hasWriteAccess = hasScope('subscriptions-write');
  const hasCreateAccess = hasAccess && hasWriteAccess;
  const {
    data,
    loading
  } = useFetchConvertableObjectInfo(objectTypeId, objectId);
  const {
    supportedCurrencies
  } = usePortalCurrencies();
  return useMemo(() => {
    const validationErrors = new Set();
    if (!objectTypeId) {
      validationErrors.add(BillingConversionError.NO_OBJECT_TYPE);
    }
    if (!objectId) {
      validationErrors.add(BillingConversionError.NO_OBJECT_ID);
    }
    if (loading) {
      validationErrors.add(BillingConversionError.STILL_LOADING);
    }
    if (!hasCreateAccess) {
      validationErrors.add(BillingConversionError.NO_CREATE_ACCESS);
    }
    if (validationErrors.size > 0) {
      return validationErrors;
    }
    getObjectConversionValidation(data, supportedCurrencies).forEach(error => {
      validationErrors.add(error);
    });
    switch (objectTypeId) {
      case DEAL_TYPE_ID:
      case QUOTE_TYPE_ID:
        break;
      default:
        validationErrors.add(BillingConversionError.UNSUPPORTED_OBJECT_TYPE);
        break;
    }
    return validationErrors;
  }, [data, hasCreateAccess, loading, objectId, objectTypeId, supportedCurrencies]);
}
export const useValidateObjectForSubscriptionConversion = useValidateObjectForBillingConversion;